import React from 'react';

import { Button, Icon, Tooltip } from '@tecma/ds';
import { useTranslation, Trans } from '@tecma/i18n';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import { applicationStateSelectors } from 'store/slices/applicationState/selectors';

import { ApartmentStatusTag } from 'components/ApartmentStatusTag';

import currencyFormatter from 'utils/currencyFormatter';
import { isExtraSpaceCellar, isExtraSpaceGarage } from 'utils/extraSpaceType';
import { getStatusColor } from 'utils/status';

import type { TooltipProps } from '@tecma/ds/lib/components/Tooltip/Tooltip';
import type { Quote } from 'types/quote';

import './card-quote.scss';
import { formatDate } from 'utils/formatDates';

const tooltipDefaultProps = {
  disableFocusListener: true,
  disableTouchListener: true,
  placement: 'top' as TooltipProps['placement'],
  componentsProps: {
    popper: {
      sx: {
        zIndex: 9999,
        pointerEvents: 'none',
      },
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, -8],
          },
        },
      ],
    },
  },
};

interface CardQuoteRequiredProps {
  onClick: (id: string) => void;
}

// Optional Props
interface CardQuoteOptionalProps {
  className?: string;
  isSelected?: boolean;
}

// Combined required and optional props to build the full prop interface
export interface CardQuoteProps extends CardQuoteRequiredProps, CardQuoteOptionalProps, Quote {}

const CardQuote: React.FC<CardQuoteProps> = ({
  id,
  name,
  price,
  rentPayments,
  status,
  statusReason,
  features,
  dateRange,
  createdOn,
  onClick,
  className,
  isSelected,
  newRequest,
}) => {
  const { t } = useTranslation();
  const area = useSelector(applicationStateSelectors.selectArea);
  const formattedPrice = currencyFormatter('EUR', price || (rentPayments?.total ?? 0));
  const classList = classNames('card-quote', {
    [`${status}`]: status,
    selected: isSelected,
    [`${className}`]: className,
  });
  const showDateRange = area === 'rent' && dateRange;
  const boxesNumber = features?.filter((feature) => isExtraSpaceGarage(feature.type)).length;
  const cellarsNumber = features?.filter((feature) => isExtraSpaceCellar(feature.type)).length;
  const quoteIcon = isSelected ? 'check-circle' : 'chevron-right';
  const SCROLL_OFFSET = 104;

  const handleLinkedQuoteClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (!newRequest?.id || typeof document === 'undefined') {
      return;
    }
    // Scroll to the linked apartment card so users can reach the new request quickly.
    const targetApartmentId = `card-apartment-${newRequest.id}`;
    let targetApartmentHeader = document.getElementById(targetApartmentId);
    if (!targetApartmentHeader) {
      targetApartmentHeader = document.querySelector(`[data-linked-quote-ids~='${newRequest.id}']`);
    }
    if (!(targetApartmentHeader instanceof HTMLElement)) return;
    const headerTop = targetApartmentHeader.getBoundingClientRect().top + window.pageYOffset - SCROLL_OFFSET;
    window.scrollTo({
      top: headerTop,
      behavior: 'smooth',
    });
  };

  return (
    <Button className={classList} color='transparent' onClick={() => onClick(id)}>
      <div className='card-quote-header'>
        {createdOn && (
          <div className='card-quote-created-on'>
            <span>{t('quote.created-on', { date: formatDate(new Date(createdOn), true, false) })}</span>
          </div>
        )}
        <div className='card-quote-header-content'>
          <span className='quote-name'>{name}</span>
          {status && status !== 'init' && <ApartmentStatusTag label={status} color={getStatusColor(status)} statusReason={statusReason} />}
        </div>
      </div>
      <div className='card-quote-content'>
        <div className='card-quote-info'>
          {showDateRange && (
            <Tooltip title={t('quote.checkInCheckOut')} {...tooltipDefaultProps}>
              <div className='quote-date-range property'>
                <Icon size='small' iconName='appointments-done' filled />
                <span>{`${formatDate(new Date(dateRange.checkInDate))} - ${formatDate(new Date(dateRange.checkOutDate))}`}</span>
              </div>
            </Tooltip>
          )}
          <div className='quote-price-container'>
            <span>{area === 'rent' ? t('quote.monthly-rate') : t('quote.total')}</span>
            <h2 className='quote-price'>
              <span className='quote-price-value'>
                <Trans
                  i18nKey='quote.price'
                  values={{ price: formattedPrice }}
                  context={area === 'rent' ? area : undefined}
                  components={{ span: <span /> }}
                />
              </span>
              <span>{t('quote.includes-taxes-and-fees')}</span>
            </h2>
          </div>
        </div>
        <Button iconName={quoteIcon} color='transparent' onClick={() => onClick(id)} />
      </div>
      <div className='card-quote-footer'>
        <div className='quote-properties'>
          {!!boxesNumber && (
            <Tooltip title={t('quote.garage')} {...tooltipDefaultProps}>
              <div className='property'>
                <div>
                  <Icon size='small' iconName='garage' filled />
                </div>
                <span>{boxesNumber}</span>
              </div>
            </Tooltip>
          )}
          {!!cellarsNumber && (
            <Tooltip title={t('quote.cellar')} {...tooltipDefaultProps}>
              <div className='property'>
                <div>
                  <Icon size='small' iconName='wine' filled />
                </div>
                <span>{cellarsNumber}</span>
              </div>
            </Tooltip>
          )}
          {!!showDateRange && (
            <Tooltip title={t('quote.checkInCheckOut')} {...tooltipDefaultProps}>
              <div className='quote-date-range property'>
                <div>
                  <Icon size='small' iconName='appointments-done' filled />
                </div>
                <span>{`${formatDate(new Date(dateRange.checkInDate))} - ${formatDate(new Date(dateRange.checkOutDate))}`}</span>
              </div>
            </Tooltip>
          )}
          <Button iconName={quoteIcon} color='transparent' onClick={() => {}} />
        </div>
        {newRequest?.id && (
          <div className='card-quote-linked-quote'>
            <Button link color='primary' className='card-quote-linked-quote-label' onClick={handleLinkedQuoteClick}>
              <span>{t('quote.go-to-new-space', { defaultValue: 'Go to the new space' })}</span>
            </Button>
          </div>
        )}
      </div>
    </Button>
  );
};

export default React.memo(CardQuote);
